import { plus } from '@0xtorch/big-decimal'
import { type CryptoCurrency, type FiatCurrency, rest } from '@0xtorch/core'
import { z } from 'zod'
import type { AssetBalance } from '../../types'
import { getAsset } from '../../utils/getAsset'
import { bitgetApiEndpoint, bitgetSymbolCryptoIdMap } from '../constants'
import { createPrivateGetHeaders } from '../utils'
import { fundingAssetSchema } from './schema'

const responseSchema = z.object({
  code: z.string(),
  msg: z.string(),
  requestTime: z.number().int(),
  data: z.array(fundingAssetSchema),
})

const urlPath = '/api/v2/account/funding-assets'

type FetchFundingAccountAssetsParameters = {
  apiKey: string
  apiSecret: string
  password: string
  proxy?: string
  proxyHeaders?: Record<string, string>
  cryptoCurrencies: readonly CryptoCurrency[]
  fiatCurrencies: readonly FiatCurrency[]
}

export const fetchFundingAccountAssets = async ({
  apiKey,
  apiSecret,
  password,
  proxy,
  proxyHeaders,
  cryptoCurrencies,
  fiatCurrencies,
}: FetchFundingAccountAssetsParameters): Promise<readonly AssetBalance[]> => {
  const timestamp = Date.now()

  const url = new URL(urlPath, bitgetApiEndpoint)
  const response = await rest(
    proxy === undefined
      ? url.toString()
      : `${proxy}${encodeURIComponent(url.toString())}`,
    {
      fetchOptions: {
        method: 'GET',
        headers: {
          ...proxyHeaders,
          ...(await createPrivateGetHeaders({
            apiKey,
            apiSecret,
            password,
            urlPath: url.pathname,
            query: '',
          })),
        },
      },
      schema: responseSchema,
    },
  )

  const assetBalances: AssetBalance[] = []
  for (const assetData of response.data) {
    const asset = getAsset({
      symbol: assetData.coin,
      cryptoCurrencies,
      fiatCurrencies,
      symbolCryptoIdMap: bitgetSymbolCryptoIdMap,
    })
    if (asset === undefined) {
      continue
    }
    const amount = plus(assetData.available, assetData.frozen)
    if (amount.value <= 0) {
      continue
    }
    assetBalances.push({
      key: 'bitget_funding',
      cryptoId: asset.type === 'CryptoCurrency' ? asset.id : undefined,
      fiatId: asset.type === 'FiatCurrency' ? asset.id : undefined,
      amount,
      timestamp,
    })
  }

  return assetBalances
}
